import { faChevronLeft, faPenToSquare, faXmark } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ApiClientWrapper } from '@spike/api-client';
import { Inbox } from '@spike/omnichannel-model';
import useIsMobile from 'hooks/useIsMobile';
import { FunctionComponent, useRef, useState } from 'react';
import Composer from '../Composer/Composer';
import InboxAvatar from '../InboxAvatar/InboxAvatar';
import RecipientPicker from './RecipientPicker';
import { Recipient, recipientToContact } from './recipient';
import styles from './NewConversationPane.module.scss';

interface NewConversationPaneProps {
    apiClientWrapper: ApiClientWrapper;
    inboxes: Array<Inbox>;
    sending: boolean;
    error: string | null;
    onSend: (recipient: Recipient, body: string, inboxId: number) => Promise<boolean>;
    /** Return true when the recipient already has a thread and it was opened instead. */
    onPickRecipient: (recipient: Recipient) => boolean | Promise<boolean>;
    onCancel: () => void;
    onBack?: () => void;
}

/**
 * The thread pane in "new conversation" mode (CUD-5587): the header holds the recipient picker
 * instead of the contact, the body stays empty, and the usual Composer sends the first message.
 * Once sent the pane becomes the real thread in place — no second surface.
 */
const NewConversationPane: FunctionComponent<NewConversationPaneProps> = props => {
    const isMobile = useIsMobile();
    const [recipient, setRecipient] = useState<Recipient | undefined>();
    const pickRequestRef = useRef(0);

    const contact = recipient ? recipientToContact(recipient) : undefined;
    const sendableInboxes = props.inboxes.filter(inbox => inbox.status === 'active' && inbox.fromIdentity);

    return (
        <section className={styles.root}>
            <header className={styles.header}>
                {props.onBack && (
                    <button
                        type="button"
                        className={styles.back}
                        onClick={props.onBack}
                        aria-label="Back to conversations"
                    >
                        <FontAwesomeIcon icon={faChevronLeft} />
                    </button>
                )}

                <span className={styles.toLabel}>To</span>

                {!recipient || !contact ? (
                    <RecipientPicker
                        apiClientWrapper={props.apiClientWrapper}
                        onPick={picked => {
                            const requestId = pickRequestRef.current + 1;
                            pickRequestRef.current = requestId;
                            void Promise.resolve(props.onPickRecipient(picked)).then(opened => {
                                if (pickRequestRef.current !== requestId || opened) return;
                                setRecipient(picked);
                            });
                        }}
                    />
                ) : (
                    <div className={styles.recipient}>
                        <InboxAvatar
                            name={contact.name}
                            isProspect={contact.type === 'prospect'}
                            size={isMobile ? 'medium' : 'large'}
                        />
                        <div className={styles.recipientInfo}>
                            <span className={styles.recipientName}>{contact.name}</span>
                            <span className={styles.recipientMeta}>
                                {recipient.kind === 'client'
                                    ? [contact.phone, contact.email].filter(Boolean).join(' · ')
                                    : 'New number — we will match it to a client or create a prospect'}
                            </span>
                        </div>
                        <button
                            type="button"
                            className={styles.change}
                            aria-label="Change recipient"
                            title="Change recipient"
                            disabled={props.sending}
                            onClick={() => setRecipient(undefined)}
                        >
                            <FontAwesomeIcon icon={faXmark} />
                        </button>
                    </div>
                )}

                <button
                    type="button"
                    className={styles.cancel}
                    disabled={props.sending}
                    onClick={props.onCancel}
                >
                    Cancel
                </button>
            </header>

            <div className={styles.empty}>
                <FontAwesomeIcon
                    icon={faPenToSquare}
                    className={styles.emptyIcon}
                />
                <p>
                    {contact
                        ? `Send a message to ${contact.name}`
                        : 'Pick a client or type a phone number to start a conversation'}
                </p>
            </div>

            {contact && (
                <Composer
                    contact={contact}
                    inboxes={sendableInboxes}
                    sending={props.sending}
                    sendError={props.error}
                    channelLabel="Send via"
                    onSend={(body, inboxId) => (recipient ? props.onSend(recipient, body, inboxId) : false)}
                />
            )}
        </section>
    );
};

export default NewConversationPane;
